<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title">{{ $t('CMS-Slots') }}</h1>
                </div>
                <div class="stage-page-header-actions">
                    <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" to="/workspace/cms">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-left"></svg-vue>
                    </router-link>
                    <router-link class="btn-stage-icon-plain" :title="$t('Slot anlegen')" :aria-label="$t('Slot anlegen')" to="/workspace/cms/slots/new">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </router-link>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto">
                        <div v-if="slots.length > 0" class="hidden sm:block">
                            <div class="align-middle inline-block min-w-full border-b border-ultralight">
                                <table class="table-listview table-listview--zebra">
                                    <thead>
                                    <tr>
                                        <th class="table-listview-l-th">{{ $t('Slot') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-32">{{ $t('Scope') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-32">{{ $t('Systemslot') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-32">{{ $t('Nutzung') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-40">{{ $t('Zulässige Module') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-48">{{ $t('Erstellt von') }}</th>
                                        <th class="table-listview-l-th whitespace-nowrap w-40">{{ $t('Update') }}</th>
                                    </tr>
                                    </thead>
                                    <tbody class="bg-white">
                                    <tr
                                        v-for="slot in slots"
                                        :key="slot.id"
                                        class="cursor-pointer"
                                        @click="$router.push('/workspace/cms/slots/' + slot.id + '/edit')"
                                    >
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div class="flex items-center">
                                                <div class="mr-3 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-700">
                                                    <svg-vue class="w-8 h-8 p-1.5 text-gray-200" icon="font-awesome.regular.check-to-slot"></svg-vue>
                                                </div>
                                                <div class="min-w-0">
                                                    <div class="text-sm font-medium text-gray-900 truncate">{{ slot.name }}</div>
                                                    <div class="text-xs text-gray-500 mt-1">{{ slot.key }}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-700">{{ scopeLabel(slot.scope) }}</td>
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div v-if="slot.is_system" class="inline-flex items-center justify-center">
                                                <svg-vue class="h-5 w-3 text-gray-400" icon="font-awesome.regular.lock"></svg-vue>
                                            </div>
                                            <div v-else class="text-sm text-gray-500">-</div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div class="text-sm text-gray-900">{{ slot.page_reference_count }}</div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-700">{{ allowedModulesCount(slot) }}</td>
                                        <td class="table-listview-l-td whitespace-nowrap">
                                            <div class="flex items-center min-w-0 max-w-[12rem] text-sm text-gray-500">
                                                <img
                                                    v-if="createdActorAvatar(slot)"
                                                    :src="createdActorAvatar(slot)"
                                                    :alt="createdActorName(slot)"
                                                    :title="createdActorName(slot)"
                                                    class="h-6 w-6 rounded-full border border-white object-cover mr-2 flex-shrink-0"
                                                >
                                                <span class="truncate text-gray-900">{{ createdActorName(slot) }}</span>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-nowrap text-sm text-gray-500">
                                            {{ slot.updated_at ? $momentFormatDateTimeAgo(slot.updated_at) : '-' }}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div v-else-if="!loading" class="h-full">
                            <empty-state />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    cmsCreatedActorAvatar,
    cmsCreatedActorName,
} from '@/utils/cms-update-meta';

export default {
    name: 'cms-slots-list',
    data() {
        return {
            loading: true,
            slots: [],
        };
    },
    mounted() {
        this.loadSlots();
    },
    methods: {
        createdActorAvatar(slot) {
            return cmsCreatedActorAvatar(slot);
        },
        createdActorName(slot) {
            return cmsCreatedActorName(slot);
        },
        loadSlots() {
            this.loading = true;
            axios.get('api/workspace/admin/cms/slots').then((response) => {
                this.slots = Array.isArray(response.data) ? response.data : [];
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
        scopeLabel(scope) {
            if (scope === 'page') {
                return this.$t('Seite');
            }

            if (scope === 'template') {
                return this.$t('Template');
            }

            return this.$t('Template, Seite');
        },
        allowedModulesCount(slot) {
            return Array.isArray(slot?.allowed_module_types) ? slot.allowed_module_types.length : 0;
        },
    },
}
</script>
